import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Button,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  ShoppingCart as ShoppingCartIcon,
  Info as InfoIcon,
  Gavel as GavelIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../contexts/AdminContext';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import AdminCardManager from './AdminCardManager';
import AdminBoxesManager from './AdminBoxesManager';
import AdminAboutUsEditor from './AdminAboutUsEditor';
import AdminLegalEditor from './AdminLegalEditor';
import AdminTermsPrivacyEditor from './AdminTermsPrivacyEditor';

const AdminDashboard: React.FC = () => {
  const { isAdmin, logoutAdmin } = useAdmin();
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode } = useAppTheme();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [recentCards, setRecentCards] = useState<any[]>([]);
  const [stats, setStats] = useState({
    totalCards: 0,
    totalValue: 0,
    pendingUpdates: 0,
  });

  // Determine colors based on current theme
  const isLight = mode === 'light';
  const bgColor = isLight ? '#FFFFFF' : '#000000';
  const sidebarBg = isLight ? '#FFFFFF' : '#0A0A0A';
  const cardBg = isLight ? '#FFFFFF' : '#0A0A0A';
  const borderColor = isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.05)';
  const textColor = isLight ? '#000000' : '#FFFFFF';

  // Check admin status on load
  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin-yyd92c'); // Redirect to login if not admin
    } else {
      // Load real data for the dashboard
      loadDashboardData();
    }
  }, [isAdmin, navigate]);

  const loadDashboardData = async () => {
    try {
      // Fetch real eBay listings data
      const response = await fetch('https://api.ebay.com/buy/browse/v1/item_summary/search?q=Football%20PSA%2010&limit=50&filter=conditions:{NEW|USED_EXCELLENT|USED_VERY_GOOD}&sort=price', {
        headers: {
          'Authorization': 'Bearer LandonEr-GoatCard-PRD-b0ba6230d-6529699b',
          'X-EBAY-C-ENDUSERCTX': 'affiliateCampaignId=5339120245',
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const items = data.itemSummaries || [];
        
        // Calculate real stats
        const totalCards = items.length;
        const totalValue = items.reduce((sum: number, item: any) => {
          return sum + (parseFloat(item.price?.value || 0));
        }, 0);
        
        // Get recent cards (last 3 items)
        const recentCardsData = items.slice(0, 3).map((item: any) => ({
          id: item.itemId,
          title: item.title,
          price: parseFloat(item.price?.value || 0),
          lastUpdated: new Date().toISOString().split('T')[0], // Today's date
          views: Math.floor(Math.random() * 2000) + 500, // Random views for demo
        }));

        setStats({
          totalCards,
          totalValue,
          pendingUpdates: Math.floor(Math.random() * 20) + 5, // Random pending updates
        });
        
        setRecentCards(recentCardsData);
      } else {
        console.error('Failed to fetch eBay data:', response.statusText);
        // Fallback to empty data
        setStats({
          totalCards: 0,
          totalValue: 0,
          pendingUpdates: 0,
        });
        setRecentCards([]);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Fallback to empty data
      setStats({
        totalCards: 0,
        totalValue: 0,
        pendingUpdates: 0,
      });
      setRecentCards([]);
    }
  };
  
  const handleLogout = () => {
    logoutAdmin();
    navigate('/');
  };

  const navItems = [
    { label: 'Dashboard', icon: <DashboardIcon />, section: 'dashboard' },
    { label: 'Card Manager', icon: <CategoryIcon />, section: 'cards' },
    { label: 'Buy Boxes', icon: <ShoppingCartIcon />, section: 'boxes' },
    { label: 'Inventory', icon: <InventoryIcon />, section: 'inventory' },
    { label: 'Users', icon: <PeopleIcon />, section: 'users' },
    { label: 'Pricing', icon: <AttachMoneyIcon />, section: 'pricing' },
    { label: 'Analytics', icon: <TrendingUpIcon />, section: 'analytics' },
    { label: 'Settings', icon: <SettingsIcon />, section: 'settings' },
    { label: 'About Us', icon: <InfoIcon />, section: 'about-us' },
    { label: 'Legal', icon: <GavelIcon />, section: 'legal' },
    { label: 'Terms & Privacy', icon: <DescriptionIcon />, section: 'terms-privacy' },
  ];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Render the main content based on active section
  const renderMainContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
                Admin Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Welcome to your secure management portal
              </Typography>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    height: '100%', 
                    borderRadius: 2,
                    backgroundColor: cardBg,
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Total Cards
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
                    {stats.totalCards}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    +24 this month
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    height: '100%', 
                    borderRadius: 2,
                    backgroundColor: cardBg,
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Total Inventory Value
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
                    {formatCurrency(stats.totalValue)}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    +{formatCurrency(4250)} this month
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    height: '100%', 
                    borderRadius: 2,
                    backgroundColor: cardBg,
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Pending Updates
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
                    {stats.pendingUpdates}
                  </Typography>
                  <Button size="small" color="primary" sx={{ fontWeight: 500 }}>
                    View Details
                  </Button>
                </Paper>
              </Grid>
            </Grid>

            {/* Recent Cards Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: textColor }}>
                  Recently Updated Cards
                </Typography>
                <Button 
                  startIcon={<AddIcon />}
                  variant="contained"
                  size="small"
                  sx={{ fontWeight: 500 }}
                  onClick={() => setActiveSection('cards')}
                >
                  Manage Cards
                </Button>
              </Box>
              
              <Grid container spacing={3}>
                {recentCards.map((card) => (
                  <Grid item xs={12} md={4} key={card.id}>
                    <Card 
                      sx={{ 
                        borderRadius: 2,
                        backgroundColor: isLight ? '#FFFFFF' : 'background.paper',
                        position: 'relative',
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          ID: {card.id}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: textColor }}>
                          {card.title}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Price
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textColor }}>
                              {formatCurrency(card.price)}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" color="text.secondary">
                              Views
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textColor }}>
                              {card.views.toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${borderColor}` }}>
                          <Typography variant="caption" color="text.secondary">
                            Last updated: {new Date(card.lastUpdated).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Quick Actions */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                borderRadius: 2,
                backgroundColor: cardBg,
                border: `1px solid ${borderColor}`,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: textColor }}>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                {['Add New Card', 'Update Prices', 'Manage Images', 'Edit Descriptions', 'Bulk Edit'].map((action, index) => (
                  <Grid item key={index}>
                    <Button
                      variant="outlined"
                      size="medium"
                      sx={{ borderRadius: 2 }}
                      onClick={() => setActiveSection('cards')}
                    >
                      {action}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </>
        );

      case 'cards':
        return <AdminCardManager />;

      case 'boxes':
        return <AdminBoxesManager />;

      case 'about-us':
        return <AdminAboutUsEditor />;

      case 'legal':
        return <AdminLegalEditor />;

      case 'terms-privacy':
        return <AdminTermsPrivacyEditor />;

      default:
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
            <Typography variant="h5" color="text.secondary">
              {navItems.find(item => item.section === activeSection)?.label} section is coming soon.
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: bgColor }}>
      {/* Side Navigation */}
      <Box
        sx={{
          width: 280,
          flexShrink: 0,
          backgroundColor: sidebarBg,
          borderRight: `1px solid ${borderColor}`,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              backgroundColor: theme.palette.primary.main,
              mr: 2,
            }}
          >
            <DashboardIcon />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: textColor }}>
            GOAT Admin
          </Typography>
        </Box>
        
        <Divider sx={{ borderColor: borderColor }} />
        
        <List sx={{ flexGrow: 1, py: 2 }}>
          {navItems.map((item) => (
            <ListItem
              key={item.section}
              button
              selected={activeSection === item.section}
              onClick={() => setActiveSection(item.section)}
              sx={{
                my: 0.5,
                mx: 1,
                borderRadius: 1,
                '&.Mui-selected': {
                  backgroundColor: isLight ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.12)',
                  '&:hover': {
                    backgroundColor: isLight ? 'rgba(25, 118, 210, 0.18)' : 'rgba(25, 118, 210, 0.18)',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} sx={{ color: textColor }} />
            </ListItem>
          ))}
        </List>
        
        <Divider sx={{ borderColor: borderColor }} />
        
        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ justifyContent: 'flex-start', py: 1 }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>
        <Container maxWidth="xl">
          {renderMainContent()}
        </Container>
      </Box>
    </Box>
  );
};

export default AdminDashboard; 